import React, { useRef, useState, useEffect } from 'react';

interface OTPInputProps {
  length?: number;
  onComplete: (otp: string) => void;
  isDisabled?: boolean;
}

export const OTPInput: React.FC<OTPInputProps> = ({
  length = 6,
  onComplete,
  isDisabled = false,
}) => {
  const [otp, setOtp] = useState<string[]>(Array(length).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  const handleChange = (index: number, value: string) => {
    if (isDisabled) return;
    const newOtp = [...otp];
    // Only allow digits
    const digit = value.replace(/\D/g, '').slice(-1);
    newOtp[index] = digit;
    setOtp(newOtp);

    // Move to next input if filled
    if (digit && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    // If all filled, trigger onComplete
    if (newOtp.every((v) => v !== '')) {
      onComplete(newOtp.join(''));
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, length);
    const digits = pastedData.replace(/\D/g, '').split('');
    const newOtp = [...otp];
    digits.forEach((digit, idx) => {
      if (idx < length) newOtp[idx] = digit;
    });
    setOtp(newOtp);
    const lastFilled = digits.length - 1;
    if (lastFilled < length - 1) {
      inputRefs.current[lastFilled + 1]?.focus();
    }
    if (newOtp.every((v) => v !== '')) {
      onComplete(newOtp.join(''));
    }
  };

  return (
    <div className="flex justify-center gap-2">
      {otp.map((digit, index) => (
        <input
          key={index}
          ref={(el) => {
            inputRefs.current[index] = el;
          }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digit}
          onChange={(e) => handleChange(index, e.target.value)}
          onKeyDown={(e) => handleKeyDown(index, e)}
          onPaste={handlePaste}
          disabled={isDisabled}
          className="h-14 w-12 rounded-lg border border-[rgb(var(--border))] bg-[rgb(var(--background))] text-center text-xl font-semibold text-[rgb(var(--foreground))] focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-60"
          aria-label={`OTP digit ${index + 1}`}
        />
      ))}
    </div>
  );
};